// Safety Settings - Crisis keywords, Perspective API, and moderation config
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import {
    doc,
    getDoc,
    setDoc,
    serverTimestamp
} from 'firebase/firestore';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Chip,
    Alert,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import WarningIcon from '@mui/icons-material/Warning';
import SecurityIcon from '@mui/icons-material/Security';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

const SafetySettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState(null);

    // Crisis keywords for auto-detection
    const [crisisKeywords, setCrisisKeywords] = useState([]);
    const [newKeyword, setNewKeyword] = useState('');

    // Perspective API settings
    const [perspectiveEnabled, setPerspectiveEnabled] = useState(false);
    const [toxicityThreshold, setToxicityThreshold] = useState(0.7);

    // Helpline settings
    const [helplines, setHelplines] = useState([]);
    const [helplineDialog, setHelplineDialog] = useState(false);
    const [newHelpline, setNewHelpline] = useState({ name: '', number: '', description: '' });

    // Default crisis keywords
    const DEFAULT_CRISIS_KEYWORDS = [
        'suicide', 'kill myself', 'end my life', 'self harm', 'hurt myself',
        'don\'t want to live', 'want to die', 'suicidal', 'cutting',
        'overdose', 'hang myself', 'jump off'
    ];

    // Default helplines
    const DEFAULT_HELPLINES = [
        { name: '988 Suicide & Crisis Lifeline', number: '988', description: 'Free 24/7 support (US)' },
        { name: 'Crisis Text Line', number: 'Text HOME to 741741', description: 'Free, confidential text support' },
        { name: 'SHARE Pregnancy & Infant Loss', number: '1-800-821-6819', description: 'Grief support for pregnancy loss' }
    ];

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const settingsDoc = await getDoc(doc(db, 'config', 'safety_settings'));

            if (settingsDoc.exists()) {
                const data = settingsDoc.data();
                setCrisisKeywords(data.crisisKeywords || DEFAULT_CRISIS_KEYWORDS);
                setPerspectiveEnabled(data.perspectiveEnabled || false);
                setToxicityThreshold(data.toxicityThreshold || 0.7);
                setHelplines(data.helplines || DEFAULT_HELPLINES);
            } else {
                // Initialize with defaults
                setCrisisKeywords(DEFAULT_CRISIS_KEYWORDS);
                setHelplines(DEFAULT_HELPLINES);
            }
        } catch (error) {
            console.error('Error loading settings:', error);
            setAlert({ severity: 'error', message: 'Failed to load settings' });
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'config', 'safety_settings'), {
                crisisKeywords,
                perspectiveEnabled,
                toxicityThreshold,
                helplines,
                updatedAt: serverTimestamp()
            });
            setAlert({ severity: 'success', message: 'Settings saved successfully' });
        } catch (error) {
            console.error('Error saving settings:', error);
            setAlert({ severity: 'error', message: 'Failed to save settings' });
        } finally {
            setSaving(false);
        }
    };

    const addKeyword = () => {
        if (newKeyword.trim() && !crisisKeywords.includes(newKeyword.trim().toLowerCase())) {
            setCrisisKeywords([...crisisKeywords, newKeyword.trim().toLowerCase()]);
            setNewKeyword('');
        }
    };

    const removeKeyword = (keyword) => {
        setCrisisKeywords(crisisKeywords.filter(k => k !== keyword));
    };

    const addHelpline = () => {
        if (newHelpline.name && newHelpline.number) {
            setHelplines([...helplines, newHelpline]);
            setNewHelpline({ name: '', number: '', description: '' });
            setHelplineDialog(false);
        }
    };

    const removeHelpline = (index) => {
        setHelplines(helplines.filter((_, i) => i !== index));
    };

    if (loading) {
        return <Typography>Loading safety settings...</Typography>;
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Safety Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure crisis detection and content moderation
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveSettings}
                    disabled={saving}
                    sx={{ bgcolor: '#FF6B9D' }}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>

            {/* Alert */}
            {alert && (
                <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Crisis Keywords */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <WarningIcon sx={{ color: '#D32F2F' }} />
                                <Typography variant="h6" fontWeight="bold">
                                    Crisis Keywords
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Messages containing these keywords will be auto-highlighted in Live Monitoring
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Add keyword..."
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                                />
                                <Button variant="outlined" onClick={addKeyword}>
                                    <AddIcon />
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%', minWidth: 0, overflow: 'hidden', alignItems: 'center' }}>
                                {crisisKeywords.map((keyword) => (
                                    <Chip
                                        key={keyword}
                                        label={keyword}
                                        onDelete={() => removeKeyword(keyword)}
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                        sx={{ m: 0, maxWidth: '100%' }}
                                    />
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Perspective API */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <SecurityIcon sx={{ color: '#4ECDC4' }} />
                                <Typography variant="h6" fontWeight="bold">
                                    Perspective API
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Automatic toxicity scoring for messages and Q&A
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={perspectiveEnabled}
                                        onChange={(e) => setPerspectiveEnabled(e.target.checked)}
                                    />
                                }
                                label="Enable Perspective API"
                            />

                            {perspectiveEnabled && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" gutterBottom>
                                        Toxicity Threshold: {(toxicityThreshold * 100).toFixed(0)}%
                                    </Typography>
                                    <TextField
                                        type="range"
                                        inputProps={{ min: 0.5, max: 0.95, step: 0.05 }}
                                        value={toxicityThreshold}
                                        onChange={(e) => setToxicityThreshold(parseFloat(e.target.value))}
                                        fullWidth
                                        size="small"
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Content above this threshold will be auto-flagged for review
                                    </Typography>
                                </Box>
                            )}

                            <Alert severity="info" sx={{ mt: 2 }}>
                                Perspective API requires a Cloud Function. See implementation guide.
                            </Alert>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Crisis Helplines */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocalHospitalIcon sx={{ color: '#96CEB4' }} />
                                    <Typography variant="h6" fontWeight="bold">
                                        Crisis Helplines
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setHelplineDialog(true)}
                                >
                                    Add Helpline
                                </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                These helplines are available when moderators escalate crisis reports
                            </Typography>

                            <List>
                                {helplines.map((helpline, index) => (
                                    <ListItem key={index} divider>
                                        <ListItemText
                                            primary={helpline.name}
                                            secondary={
                                                <>
                                                    <Typography component="span" variant="body2" color="primary">
                                                        {helpline.number}
                                                    </Typography>
                                                    {helpline.description && ` — ${helpline.description}`}
                                                </>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" onClick={() => removeHelpline(index)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Add Helpline Dialog */}
            <Dialog open={helplineDialog} onClose={() => setHelplineDialog(false)}>
                <DialogTitle>Add Crisis Helpline</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Name"
                        value={newHelpline.name}
                        onChange={(e) => setNewHelpline({ ...newHelpline, name: e.target.value })}
                        margin="normal"
                        placeholder="e.g., National Suicide Prevention Lifeline"
                    />
                    <TextField
                        fullWidth
                        label="Number/Contact"
                        value={newHelpline.number}
                        onChange={(e) => setNewHelpline({ ...newHelpline, number: e.target.value })}
                        margin="normal"
                        placeholder="e.g., 988 or Text HOME to 741741"
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={newHelpline.description}
                        onChange={(e) => setNewHelpline({ ...newHelpline, description: e.target.value })}
                        margin="normal"
                        placeholder="e.g., Free 24/7 support"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelplineDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={addHelpline}>Add</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SafetySettings;
